import React, { useState } from 'react';
import {
  Paper,
  Typography,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Box,
} from '@mui/material';
import { KeyboardArrowDownOutlined, KeyboardArrowUpOutlined } from '@mui/icons-material';

const equipmentOptionsMap: Record<string, string> = {
  'Balança': 'Balança',
  'Câmaras frias': 'Câmaras frias',
  'Banheiros': 'Banheiros',
  'Pias': 'Pias',
  'Bancas de exposição': 'Bancas de exposição',
  'Outro': 'Outro',
};

const frequencyOptionsMap: Record<string, string> = {
  'Diária': 'Diária',
  'Semanal': 'Semanal',
  'Quinzenal': 'Quinzenal',
  'Mensal': 'Mensal',
  'Outro': 'Outro',
};

const publicOptionsMap: Record<string, string> = {
  'Consumidores locais': 'Consumidores locais',
  'Turistas': 'Turistas',
};

const formatCurrency = (value: number) =>
  typeof value === 'number'
    ? (value / 100).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })
    : 'Não informado';

interface PermanentMarket {
  name: string;
  address: string;
  neighborhood: string;
  zone: string;
  hasInfrastructure: boolean | null;
  equipments: string[];
  otherEquipment: string;
  frequency: string;
  otherFrequency: string;
  predominantPublic: string;
  numFamilyFarmProducers: number;
  numWomenFamilyFarmProducers: number;
  numFamilyFarmResellers: number;
  numWomenFamilyFarmResellers: number;
  numHandicraftSellers: number;
  numWomenHandicraftSellers: number;
  numExternalIndustrialSellers: number;
  annualMarketValue: number;
  totalFamilyFarmingValue: number;
  totalMarketValue: number;
}

interface Step3SimViewProps {
  data: {
    referenceYear: number;
    permanentMarkets: PermanentMarket[];
  };
}

const Step3SimView: React.FC<Step3SimViewProps> = ({ data }) => {
  const [open, setOpen] = useState<boolean>(true);

  return (
    <Paper elevation={1} sx={{ p: 0, mt: 2 }}>
      <Box
        sx={{ px: 3, pt: 2, cursor: 'pointer', userSelect: 'none' }}
        onClick={() => setOpen(!open)}
      >
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <Typography
            variant="subtitle1"
            sx={{
              fontWeight: 500,
              color: "#000000DE",
              fontSize: "20px",
              mb: 1,
              display: "flex",
              alignItems: "center",
              gap: 0.5,
            }}
          >
            {open ? (
              <KeyboardArrowUpOutlined
                sx={{ color: "text.secondary", fontSize: 20 }}
              />
            ) : (
              <KeyboardArrowDownOutlined
                sx={{ color: "text.secondary", fontSize: 20 }}
              />
            )}
            Feiras Permanentes
          </Typography>
        </Box>
        <Divider sx={{ mb: 2 }} />
      </Box>

      {open && (
        <Box sx={{ p: 3 }}>
          {(!data.permanentMarkets || data.permanentMarkets.length === 0) ? (
            <Typography color="text.secondary">
              Não há feiras deste tipo cadastradas para este município.
            </Typography>
          ) : (
            <TableContainer component={Paper} elevation={0}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell><strong>Nome</strong></TableCell>
                    <TableCell><strong>Bairro</strong></TableCell>
                    <TableCell><strong>Zona</strong></TableCell>
                    <TableCell><strong>Endereço</strong></TableCell>
                    <TableCell><strong>Infraestrutura física</strong></TableCell>
                    <TableCell><strong>Equipamentos</strong></TableCell>
                    <TableCell><strong>Frequência</strong></TableCell>
                    <TableCell><strong>Público predominante</strong></TableCell>
                    <TableCell align="center"><strong>Produtores AF</strong></TableCell>
                    <TableCell align="center"><strong>Mulheres Produtoras AF</strong></TableCell>
                    <TableCell align="center"><strong>Revendedores AF</strong></TableCell>
                    <TableCell align="center"><strong>Mulheres Revendedoras AF</strong></TableCell>
                    <TableCell align="center"><strong>Com. Artesanato</strong></TableCell>
                    <TableCell align="center"><strong>Mulheres Artesanato</strong></TableCell>
                    <TableCell align="center"><strong>Com. beneficiados/industrializados</strong></TableCell>
                    <TableCell align="center"><strong>Valor anual (R$)</strong></TableCell>
                    <TableCell align="center"><strong>Valor AF (R$)</strong></TableCell>
                    <TableCell align="center"><strong>Valor total feira (R$)</strong></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {data.permanentMarkets.map((market, idx) => (
                    <TableRow key={idx}>
                      <TableCell>{market.name || 'Não informado'}</TableCell>
                      <TableCell>{market.neighborhood || 'Não informado'}</TableCell>
                      <TableCell>{market.zone || 'Não informado'}</TableCell>
                      <TableCell>{market.address || 'Não informado'}</TableCell>
                      <TableCell>
                        {market.hasInfrastructure === true
                          ? 'Sim'
                          : market.hasInfrastructure === false
                            ? 'Não'
                            : 'Não informado'}
                      </TableCell>
                      <TableCell>
                        {market.equipments && market.equipments.length > 0 ? (
                          <>
                            {market.equipments.map((eq, i) => (
                              <Chip
                                key={i}
                                label={equipmentOptionsMap[eq] || eq}
                                sx={{ mr: 0.5, mb: 0.5 }}
                              />
                            ))}
                            {market.equipments.includes('Outro') && market.otherEquipment && (
                              <Chip
                                label={market.otherEquipment}
                                color="secondary"
                                sx={{ mr: 0.5, mb: 0.5 }}
                              />
                            )}
                          </>
                        ) : (
                          <Typography variant="body2" color="text.secondary">
                            Não informado
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        {market.frequency ? (
                          <>
                            <Chip
                              label={frequencyOptionsMap[market.frequency] || market.frequency}
                              sx={{ mr: 0.5, mb: 0.5 }}
                            />
                            {market.frequency === 'Outro' && market.otherFrequency && (
                              <Chip
                                label={market.otherFrequency}
                                color="secondary"
                                sx={{ mr: 0.5, mb: 0.5 }}
                              />
                            )}
                          </>
                        ) : (
                          <Typography variant="body2" color="text.secondary">
                            Não informado
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        {publicOptionsMap[market.predominantPublic] || market.predominantPublic || 'Não informado'}
                      </TableCell>
                      <TableCell align="center">{market.numFamilyFarmProducers ?? 'Não informado'}</TableCell>
                      <TableCell align="center">{market.numWomenFamilyFarmProducers ?? 'Não informado'}</TableCell>
                      <TableCell align="center">{market.numFamilyFarmResellers ?? 'Não informado'}</TableCell>
                      <TableCell align="center">{market.numWomenFamilyFarmResellers ?? 'Não informado'}</TableCell>
                      <TableCell align="center">{market.numHandicraftSellers ?? 'Não informado'}</TableCell>
                      <TableCell align="center">{market.numWomenHandicraftSellers ?? 'Não informado'}</TableCell>
                      <TableCell align="center">{market.numExternalIndustrialSellers ?? 'Não informado'}</TableCell>
                      <TableCell align="center">{formatCurrency(market.annualMarketValue)}</TableCell>
                      <TableCell align="center">{formatCurrency(market.totalFamilyFarmingValue)}</TableCell>
                      <TableCell align="center">{formatCurrency(market.totalMarketValue)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Box>
      )}
    </Paper>
  );
}

export default Step3SimView;
